import { RefObject, useEffect } from 'react';
import { getFocusableElements, resolveActiveFocusable } from './focusable';

/** Keep keyboard focus inside an open dialog/modal. */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  initialFocusRef?: RefObject<HTMLElement | null>,
  /** Prefer this trigger on close (mouse open may not leave focus on it). */
  returnFocusRef?: RefObject<HTMLElement | null>
) {
  useEffect(() => {
    if (!active) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    let rafId = 0;
    let mounted = true;

    const focusInitial = () => {
      if (!mounted) return;

      const container = containerRef.current;
      if (!container) {
        rafId = requestAnimationFrame(focusInitial);
        return;
      }

      const initial = initialFocusRef?.current;
      if (initial && container.contains(initial)) {
        initial.focus();
        return;
      }

      const focusables = getFocusableElements(container);
      if (!focusables.length) {
        rafId = requestAnimationFrame(focusInitial);
        return;
      }

      const autoFocused = focusables.find(el =>
        el.matches(':focus-visible, :focus')
      );
      (autoFocused ?? focusables[0]).focus();
    };

    rafId = requestAnimationFrame(focusInitial);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const container = containerRef.current;
      if (!container) return;

      const focused = document.activeElement;
      // Nested dialogs (e.g. Documents over Navigation) portaled outside this
      // container — let the inner dialog handle Tab instead.
      if (!(focused instanceof HTMLElement) || !container.contains(focused)) {
        return;
      }

      const focusables = getFocusableElements(container);
      if (!focusables.length) return;

      event.preventDefault();

      const activeEl = resolveActiveFocusable(container, focusables);
      let index = activeEl ? focusables.indexOf(activeEl) : -1;

      if (index === -1) {
        focusables[event.shiftKey ? focusables.length - 1 : 0].focus();
        return;
      }

      if (event.shiftKey) {
        index = index <= 0 ? focusables.length - 1 : index - 1;
      } else {
        index = index >= focusables.length - 1 ? 0 : index + 1;
      }

      focusables[index].focus();
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      mounted = false;
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', onKeyDown, true);
      (returnFocusRef?.current ?? previouslyFocused)?.focus?.();
    };
  }, [active, containerRef, initialFocusRef, returnFocusRef]);
}
